import React, {
  type ComponentType,
  PureComponent,
  type ReactElement,
  type ReactNode,
} from 'react';

import classNames from 'classnames';
import {
  difference,
  identity,
  isEqual,
  isString,
  noop,
  pick,
  uniqueId,
} from 'lodash-es';

import ErrorBoundary from '../../../components/error_boundary/error_boundary';
import TooltipTrigger from '../../../components/tooltip_trigger/tooltip_trigger';
import { ICONS } from '../../../icons/icon';
import { ui } from '../../../utils/helpers';
import IconButton from '../../icon_button/icon_button';
import FormFieldLabel from '../components/form_field_label/form_field_label';
import type { FieldProps, FormContextType } from '../form_context/form_context';
import type { FormFieldEvent } from '../types';

import styles from './form_field.module.css';

type State<V> = {
  value?: V;
  serverErrors?: string[];
  validationErrors: string[] | null;
};

export class Field<V, CP> extends PureComponent<FieldProps<V, CP>, State<V>> {
  static defaultProps = {
    setInitialValue: noop,
    setValue: noop,
    unsetValue: noop,
    onChange: noop,
    filterValue: identity,
    onFocus: noop,
    onBlur: noop,
    validate: noop,
  };

  state: State<V>;

  fieldId: string;

  value?: V;

  constructor(props: FieldProps<V, CP>) {
    super(props);

    this.fieldId = uniqueId(props.name ? `${props.name}_` : 'field_');

    const filteredValue = this.filterValue(props.value);
    this.state = {
      value: props.value ? filteredValue : props.value,
      serverErrors: props.errors,
      validationErrors: null,
    };
    // this.value is used to avoid async issues regarding state
    this.value = props.value ? filteredValue : props.value;
  }

  componentDidMount(): void {
    // forms with no name can live within the form, but they will be detached from the model
    // some fields are meant to change the state of the form but shouldn't be part of the model
    if (this.props.name && this.props.setInitialValue) {
      this.props.setInitialValue(this.value, this.props);
    }
    if (this.value && this.props.validateOnChange) {
      this.validate(this.value);
    }
  }

  componentWillUnmount(): void {
    // Drop the field's value from the model so an unmounted field isn't resubmitted.
    if (this.props.name && this.props.unsetValue) {
      this.props.unsetValue(this.props);
    }
  }

  componentDidUpdate(prevProps: Readonly<FieldProps<V, CP>>): void {
    if (!isEqual(prevProps.value, this.props.value)) {
      this.setValue(this.props.value);
    }
    if (this.props.isFormSubmitted && !prevProps.isFormSubmitted) {
      this.validate(this.value);
    }
    if (!isEqual(this.props.errors, prevProps.errors)) {
      this.showServerErrors();
    }
    if (this.props.isReset && !prevProps.isReset) {
      this.resetValue();
    }
  }

  resetValue(): void {
    if (this.props.name) {
      this.setState({ value: this.props.defaultValue });
      this.value = this.props.defaultValue;
      this.setValue(this.props.defaultValue);
    }
  }

  handleChange = (value: V | undefined, event?: FormFieldEvent): void => {
    // Event is used to separate user actions from all actions in the form component
    // and in onChange we think that the action was performed by user explicitly.
    // That's just a hack for form fields which don't pass an event object to onChange
    // callback.
    this.setValue(value, event ?? new Event('change'));
  };

  setValue(inputValue: V | undefined, event?: FormFieldEvent): void {
    const value = this.filterValue(inputValue);
    // save value in state
    this.setState({ value, serverErrors: undefined });
    this.value = value;
    // save value in model
    if (this.props.name && this.props.setValue) {
      this.props.setValue(value, this.props, event);
    }
    // validation
    if (this.state.validationErrors || this.props.validateOnChange) {
      this.validate(value, event);
    }
    // additional control
    if (this.props.onChange) {
      this.props.onChange(value, event);
    }
  }

  filterValue(inputValue?: V): V | undefined {
    const filterValue = this.props.filterValue;
    const value = filterValue ? filterValue(inputValue) : inputValue;
    return value;
  }

  handleFocus = (_value: V | undefined, event: FormFieldEvent): void => {
    if (this.props.onFocus) {
      this.props.onFocus(this.value, event);
    }
  };

  handleBlur = (_value: V | undefined, event: FormFieldEvent): void => {
    if (!this.props.validateOnChange) {
      this.validate(this.value, event);
    }
    if (this.props.onBlur) {
      this.props.onBlur(this.value, event);
    }
  };

  validate(value?: V, event?: FormFieldEvent): void {
    if (this.props.validate) {
      const validationErrors = this.props.validate(value, this.props, event);
      if (difference(validationErrors, this.state.validationErrors || [])) {
        this.setState({ validationErrors }, () =>
          this.props.onAfterValidation?.(validationErrors),
        );
      }
    }
  }

  showServerErrors(): void {
    this.setState({ serverErrors: this.props.errors });
  }

  isDisabled(): boolean {
    const { isFormDisabled, disabled, readOnly } = this.props;
    return Boolean(isFormDisabled || disabled || readOnly);
  }

  getLabel(): ReactNode {
    const {
      label,
      labelWidth,
      required,
      hideAsterisk,
      labelComponent: LabelComponent,
      updateModeComponent: UpdateModeComponent,
      labelClassName,
      labelAdornment,
      vertical,
    } = this.props;

    if (LabelComponent && UpdateModeComponent) {
      return (
        <div className={labelClassName}>
          <LabelComponent {...this.props} htmlFor={this.fieldId} />
          <UpdateModeComponent {...this.props} />
        </div>
      );
    }

    if (LabelComponent) {
      return <LabelComponent {...this.props} htmlFor={this.fieldId} />;
    }

    if (label) {
      return (
        <FormFieldLabel
          htmlFor={this.fieldId}
          required={required && !hideAsterisk}
          width={labelWidth}
          className={classNames(labelClassName, {
            [styles.vertical_label]: vertical,
          })}
          onClick={(e) => this.isDisabled() && e.preventDefault()}
          adornment={labelAdornment}
        >
          {label}
        </FormFieldLabel>
      );
    }
    return false;
  }

  render(): ReactNode {
    const {
      helpMessage,
      canClear,
      help,
      name,
      type,
      buttonAfter,
      buttonAfterControlGroupClassName,
      transformProps,
      className,
      controlClassName,
      hideErrorMessages,
      component: WrappedComponent,
      autoComplete,
      required,
      aria,
      vertical,
      helpIconMessage,
    } = this.props;
    const { validationErrors, serverErrors, value } = this.state;
    const errors =
      validationErrors ||
      (isString(serverErrors) ? [serverErrors] : serverErrors);
    const hasError = (errors?.length ?? 0) !== 0;
    const hidden = type === 'hidden' ? styles.hidden : undefined;
    // The field supplies name, value, id and the handlers below. Anything else
    // the wrapped component requires has to come from transformProps, and only
    // the caller that paired the two knows whether it does.
    // biome-ignore lint/plugin: see above.
    const propsTransform = (transformProps?.(this.props) ?? {}) as CP;
    const ControlGroup = buttonAfter ? 'div' : React.Fragment;
    const controlGroupProps = buttonAfter
      ? { className: buttonAfterControlGroupClassName ?? styles.control_group }
      : {};

    let errorMessage: ReactElement[] | undefined;
    let helpMessageContainer: ReactElement | undefined;
    let helpIconContainer: ReactElement | undefined;
    let ariaDescribedBy: string | undefined;
    if (errors?.length && !hideErrorMessages) {
      ariaDescribedBy = `error_0_${this.fieldId}`;
      errorMessage = errors.map((error, index) => (
        <div
          id={`error_${index}_${this.fieldId}`}
          className={styles.error}
          role="alert"
          key={index}
        >
          {error}
        </div>
      ));
    }

    if (helpMessage) {
      ariaDescribedBy = `help_message_${this.fieldId}`;
      helpMessageContainer = (
        <div
          className={classNames(styles.help, {
            [styles.help_message_with_error]: errorMessage,
          })}
          id={ariaDescribedBy}
        >
          {helpMessage}
        </div>
      );
    }

    if (helpIconMessage) {
      helpIconContainer = (
        <TooltipTrigger
          tooltipText={helpIconMessage}
          placement="left"
          showTooltipOnTouchDevice
        >
          <IconButton
            icon={ICONS.Help}
            variant="info"
            size={18}
            className={styles.help_icon}
          />
        </TooltipTrigger>
      );
    }

    let content = (
      <div
        {...ui('field', { error: hasError, name })}
        className={classNames(
          styles.root,
          { [styles.vertical]: vertical },
          hidden,
          className,
        )}
      >
        {this.getLabel()}
        <div className={classNames(styles.control, controlClassName)}>
          <ControlGroup {...controlGroupProps}>
            <WrappedComponent
              {...propsTransform}
              name={name}
              value={value}
              id={this.fieldId}
              onChange={this.handleChange}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              canClear={canClear}
              autoComplete={autoComplete ?? 'off'}
              disabled={this.isDisabled()}
              readOnly={this.isDisabled()}
              error={hasError}
              aria={{
                ...aria,
                invalid: hasError,
                required,
                describedby: ariaDescribedBy,
              }}
            >
              {this.props.children}
            </WrappedComponent>
            {buttonAfter}
          </ControlGroup>
          {errorMessage}
          {helpMessageContainer}
        </div>
        {helpIconContainer}
      </div>
    );

    if (isString(help)) {
      content = <TooltipTrigger tooltipText={help}>{content}</TooltipTrigger>;
    }

    return <ErrorBoundary>{content}</ErrorBoundary>;
  }
}

export type FieldOptions<V, CP, DV extends V> = {
  defaultValue?: DV;
  transformProps?: (props: FieldProps<V, CP> & CP) => CP;
};

export type FieldHocProps<V, CP> = Omit<
  FieldProps<V, CP>,
  'errors' | 'component'
> & {
  errors?: Record<string, string[]>;
};

type OmitChangeBlur<CP> = Omit<CP, 'onChange' | 'onBlur'>;

export const pickProps = <
  Props extends object,
  PropsToPick extends readonly (keyof Props)[],
>(
  props: Props,
  propsToPick: PropsToPick,
): Pick<Props, PropsToPick[number]> => pick(props, propsToPick);

type PropsWithValue<CP, V> = CP & { value?: V };

function formField<V, CP, DV extends V>(
  Input: ComponentType<PropsWithValue<CP, V>>,
  fieldOptions: FieldOptions<V, CP, DV> = {},
) {
  return (
    props: FieldHocProps<V, PropsWithValue<CP, V>> &
      FormContextType &
      OmitChangeBlur<CP>,
  ): ReactElement => {
    const errors: Record<string, string[]> = props.errors || {};
    const name = props.name;
    const resetVal =
      props.resetValues && name ? props.resetValues[name] : undefined;
    const defaultValue = resetVal || fieldOptions.defaultValue;
    return (
      <Field<V, PropsWithValue<CP, V>>
        {...props}
        errors={name ? errors[name] : []}
        component={Input}
        value={props.value ?? defaultValue}
        defaultValue={defaultValue}
        transformProps={fieldOptions.transformProps}
        // vertical is a prop on the field itself, isVertical comes from the Form context if form is in vertical mode
        vertical={props.vertical || props.isVertical}
      />
    );
  };
}

export default formField;
